import React from 'react';
import styled from 'styled-components';

import { currencyNumberFormat } from '../../../utilities/number-format';
import { formatDate } from '../utils/format-date';
import InvoiceDetails from '../types/invoices-details';

const renderLineItemsRows = (lineItems: InvoiceDetails['lineItems']) =>
  lineItems.map((lineItem, key) => (
    <tr key={key}>
      <td>
        <p>
          <strong>{lineItem.name}</strong>
        </p>
        <p>{lineItem.description}</p>
        {!!lineItem.cptCode && <p>CPT Code: {lineItem.cptCode}</p>}
        {!!lineItem.diagnosisCodes && <p>Diagnoses Codes: {lineItem.diagnosisCodes}</p>}
      </td>
      <td>{currencyNumberFormat(lineItem.amount)}</td>
    </tr>
  ));

const renderDiscountRow = (discount: NonNullable<InvoiceDetails['discount']>) => (
  <tr>
    <th>Discount for {discount.reason}</th>
    <td>(-{currencyNumberFormat(discount.amount)})</td>
  </tr>
);

const renderInsuranceAdjustmentsRow = (amount: number) => (
  <tr>
    <th>Insurance Adjustments</th>
    <td>(-{currencyNumberFormat(amount)})</td>
  </tr>
);

const renderAppliedCreditsRow = (appliedCredits: InvoiceDetails['appliedCredits']) =>
  appliedCredits.map((credit, key) => (
    <tr key={key}>
      <th>Applied Credit</th>
      <td>-{currencyNumberFormat(credit.amount)}</td>
    </tr>
  ));

const renderPaymentRows = (payments: InvoiceDetails['payments']) =>
  payments.map((payment, key) => (
    <tr key={key}>
      <th>
        {payment.reason} {payment.type} <PaymentDate>({formatDate(payment.date)})</PaymentDate>
      </th>
      <td>
        {payment.type === 'Payment' ? '-' : ''}
        {currencyNumberFormat(payment.amount)}
      </td>
    </tr>
  ));

const renderConvertedCreditsRow = (amount: number) => (
  <tr>
    <th>Converted Credit</th>
    <td>{currencyNumberFormat(amount)}</td>
  </tr>
);
interface Props {
  className?: string;
  isAzInvoice: boolean;
  lineItems: InvoiceDetails['lineItems'];
  payments: InvoiceDetails['payments'];
  discount: InvoiceDetails['discount'];
  appliedCredits: InvoiceDetails['appliedCredits'];
  lineItemsTotalAmount: number;
  invoiceAdjustmentsAmount: number;
  kindbodyRateAmount: number;
  balanceDueAmount: number;
  convertedCreditsAmount: number;
  insuranceAdjustmentsAmount: number;
}

const LineItemsTable = ({
  className,
  isAzInvoice,
  lineItems,
  payments,
  discount,
  lineItemsTotalAmount,
  invoiceAdjustmentsAmount,
  balanceDueAmount,
  kindbodyRateAmount,
  convertedCreditsAmount,
  insuranceAdjustmentsAmount,
  appliedCredits
}: Props) => (
  <Table className={className}>
    <thead>
      <tr>
        <th>Item</th>
        <th>Rate</th>
      </tr>
    </thead>
    <tbody>{renderLineItemsRows(lineItems)}</tbody>
    <tfoot>
      <tr>
        <th>Line Items Total</th>
        <td>{currencyNumberFormat(lineItemsTotalAmount)}</td>
      </tr>
      {!isAzInvoice && (
        <tr>
          <th>Invoice Adjustments</th>
          <td>{currencyNumberFormat(invoiceAdjustmentsAmount)}</td>
        </tr>
      )}
      {!isAzInvoice && (
        <tr>
          <th>Kindbody Rate (Patient Total) </th>
          <td>{currencyNumberFormat(kindbodyRateAmount)}</td>
        </tr>
      )}

      {discount && renderDiscountRow(discount)}

      {insuranceAdjustmentsAmount > 0 && renderInsuranceAdjustmentsRow(insuranceAdjustmentsAmount)}

      {renderAppliedCreditsRow(appliedCredits)}

      {renderPaymentRows(payments)}

      {convertedCreditsAmount > 0 && renderConvertedCreditsRow(convertedCreditsAmount)}

      <tr>
        <th>Balance Due</th>
        <td>{currencyNumberFormat(balanceDueAmount)}</td>
      </tr>
    </tfoot>
  </Table>
);

export default LineItemsTable;

const Table = styled.table`
  width: 100%;
  border-spacing: 0px;

  ${({ theme }) => theme.fonts.FoundersGrotesk};
  font-size: 13px;

  @media ${({ theme }) => theme.queries.minTablet} {
    font-size: 16px;
  }

  td,
  th {
    padding: 6px;

    text-align: start;
    vertical-align: middle;
  }

  thead {
    background-color: #f3e7da;

    th {
      border-bottom: 1px solid #f3e7da;

      font-weight: 600;
    }
  }

  tbody td {
    border-bottom: 1px solid #f3e7da;
  }

  tfoot {
    background-color: ${({ theme }) => theme.colors.beigeLight};

    th {
      text-align: end;
    }
  }
`;

const PaymentDate = styled.span`
  font-weight: 500;
`;
